import { Close, Download, Map, PictureAsPdf } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import JSZip from 'jszip';
import * as shpWrite from '@mapbox/shp-write';
import { useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import useGentian from '../../core/hooks/useGentian';
import { IProject } from '../../core/interfaces/project.interface';
import { renamePdfTitle } from '../../Utils/pdf-title.utils';
import {
  deliverablesZipName,
  downloadStemFor,
  fileNameOf,
  findReportKey,
  reportFileName,
  reportTitleHint,
  resolveBaseName,
  selectGeoJsonKeys,
  unmappedGeoJsonNames,
} from '../../Utils/deliverables.utils';

export interface DownloadDeliverablesModalProps {
  open: boolean;
  onClose: () => void;
  project?: IProject;
  /** Surfaces success/failure through the caller's snackbar. */
  onNotify?: (message: string) => void;
}

type GisFormat = 'geojson' | 'shapefile';

interface DeliverableFile {
  name: string;
  /** What both JSZip and the Blob constructor accept, so bytes go either way unconverted. */
  content: Blob | ArrayBuffer | Uint8Array | string;
}

const modalStyle = {
  minHeight: '400px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
  outline: 'none',
  borderRadius: 2,
};

const optionBoxStyle = { border: '1px solid #e0e0e0', borderRadius: 1, px: 2, py: 1 };

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }, 100);
};

/**
 * Converts one GeoJSON into its Shapefile components.
 *
 * shp-write only emits a zip, and names its entries by geometry type, so we unpack it and rename
 * against our own stem. A layer with a single geometry type — which is what the pipeline produces —
 * yields `<stem>.shp/.shx/.dbf/.prj`; a mixed-geometry layer keeps the type in the name so the
 * files cannot collide. The default projection is WGS84, which is what the coordinates already are.
 */
const toShapefileFiles = async (
  geojson: GeoJSON.FeatureCollection,
  stem: string,
): Promise<DeliverableFile[]> => {
  const archive = (await shpWrite.zip(geojson, {
    outputType: 'arraybuffer',
    compression: 'STORE',
  })) as ArrayBuffer;

  const unpacked = await JSZip.loadAsync(archive);
  const entries = Object.values(unpacked.files).filter((entry) => !entry.dir);
  const geometryTypes = new Set(entries.map((entry) => fileNameOf(entry.name).replace(/\.[^.]+$/, '')));

  return Promise.all(
    entries.map(async (entry) => {
      const entryName = fileNameOf(entry.name);
      const geometryType = entryName.replace(/\.[^.]+$/, '');
      const extension = entryName.slice(geometryType.length);

      return {
        name: geometryTypes.size > 1 ? `${stem}_${geometryType}${extension}` : `${stem}${extension}`,
        content: await entry.async('arraybuffer'),
      };
    }),
  );
};

export const DownloadDeliverablesModal: React.FC<DownloadDeliverablesModalProps> = ({
  open,
  onClose,
  project,
  onNotify,
}) => {
  const { generatePdfDownloadUrl, listDeliverables } = useGentian();

  const [gisSelected, setGisSelected] = useState(true);
  const [gisFormat, setGisFormat] = useState<GisFormat>('geojson');
  const [reportSelected, setReportSelected] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const selectedCount = Number(gisSelected) + Number(reportSelected);

  /** Presigned-URL fetch — the same route the report download has always used. */
  const fetchObject = async (key: string): Promise<Blob> => {
    const response = await generatePdfDownloadUrl.mutateAsync(key, {
      onError: (error) => {
        datadogLogs.logger.error('Download failed', { error, s3Key: key, time: new Date().toISOString() });
      },
    });

    if (!response?.url) throw new Error(`No download URL for ${key}`);

    const objectResponse = await fetch(response.url);
    if (!objectResponse.ok) throw new Error(`Failed to fetch ${fileNameOf(key)}`);

    return objectResponse.blob();
  };

  const collectGisFiles = async (keys: string[], baseName: string): Promise<DeliverableFile[]> => {
    const collected = await Promise.all(
      keys.map(async (key) => {
        const stem = downloadStemFor(fileNameOf(key), baseName);
        const blob = await fetchObject(key);

        if (gisFormat === 'geojson') {
          return [{ name: `${stem}.geojson`, content: blob }];
        }

        return toShapefileFiles(JSON.parse(await blob.text()), stem);
      }),
    );

    return collected.flat();
  };

  const handleDownload = async () => {
    if (!selectedCount || !project?._id) return;
    setIsDownloading(true);

    try {
      const listing = await listDeliverables.mutateAsync(project._id);
      const keys = listing?.files ?? [];

      if (!keys.length) {
        onNotify?.('No files are available for this project yet. Please contact support.');
        return;
      }

      const baseName = resolveBaseName(keys, project.name);
      const files: DeliverableFile[] = [];

      if (gisSelected) {
        const geoJsonKeys = selectGeoJsonKeys(keys);
        if (!geoJsonKeys.length) throw new Error('No GIS files found for this project');

        // Only supported outputs are downloaded. Anything else is an engine input or working file,
        // but log it so a genuinely new output type gets noticed rather than silently skipped.
        const unmapped = unmappedGeoJsonNames(keys);
        if (unmapped.length) {
          datadogLogs.logger.info('Ignoring unrecognised GeoJSON in deliverables folder', {
            name: 'handleDownload',
            projectId: project._id,
            unmapped,
          });
        }

        files.push(...(await collectGisFiles(geoJsonKeys, baseName)));
      }

      if (reportSelected) {
        const reportKey = findReportKey(keys);
        if (!reportKey) throw new Error('No report PDF found for this project');

        // Named from the project record like everything else, so a renamed project's report is
        // delivered under its current name rather than the one it carried when the survey ran.
        // The name printed inside the document is rewritten to match; if that fails the original
        // report is still delivered, just showing the older name.
        const report = new Uint8Array(await (await fetchObject(reportKey)).arrayBuffer());
        const retitled = renamePdfTitle(report, project.name, {
          expectedTitle: reportTitleHint(fileNameOf(reportKey)),
          onError: (error) =>
            datadogLogs.logger.warn('Could not rewrite the report title; delivering it unchanged', {
              name: 'handleDownload',
              projectId: project._id,
              error,
            }),
        });

        files.push({ name: reportFileName(baseName), content: retitled });
      }

      // A lone PDF is handed over as-is; everything else is bundled, since the GIS export is always
      // several files and the user should only ever get one thing out of the browser.
      const isSinglePdf = reportSelected && !gisSelected;

      if (isSinglePdf) {
        const [report] = files;
        // Wrapped rather than cast: the report is a Uint8Array once its title has been rewritten,
        // and createObjectURL takes only a Blob.
        triggerBrowserDownload(new Blob([report.content], { type: 'application/pdf' }), report.name);
      } else {
        const zip = new JSZip();
        files.forEach((file) => zip.file(file.name, file.content));
        triggerBrowserDownload(await zip.generateAsync({ type: 'blob' }), deliverablesZipName(baseName));
      }

      onNotify?.(isSinglePdf ? 'Report downloaded successfully' : 'Deliverables downloaded successfully');
      onClose();
    } catch (error) {
      datadogLogs.logger.error('Error downloading deliverables', {
        error,
        name: 'handleDownload',
        projectId: project?._id,
        gisSelected,
        gisFormat,
        reportSelected,
      });
      onNotify?.('Failed to download the file. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={isDownloading ? undefined : onClose} PaperProps={{ sx: { margin: '0' } }}>
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Download files</Typography>
          <IconButton onClick={onClose} disabled={isDownloading}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2, mt: 2 }} />

        <Box
          sx={{
            padding: 2,
            marginTop: 2,
            minHeight: '320px',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={{ mb: 2, textAlign: 'center' }}>Select what you would like to download</Typography>

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={optionBoxStyle}>
              <FormControlLabel
                control={
                  <Checkbox checked={gisSelected} onChange={(e) => setGisSelected(e.target.checked)} />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Map fontSize="small" />
                    <Box>
                      <Typography variant="body1">GIS files</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Habitat layers from your survey
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              {/* Radios rather than checkboxes: a format is always selected, so "GIS with no
                  format" is not a state that can be reached. */}
              <RadioGroup
                row
                value={gisFormat}
                onChange={(e) => setGisFormat(e.target.value as GisFormat)}
                sx={{ pl: 4 }}
              >
                <FormControlLabel
                  value="geojson"
                  disabled={!gisSelected}
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">GeoJSON</Typography>}
                />
                <FormControlLabel
                  value="shapefile"
                  disabled={!gisSelected}
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Shapefile</Typography>}
                />
              </RadioGroup>
            </Box>

            <Box sx={optionBoxStyle}>
              <FormControlLabel
                control={
                  <Checkbox checked={reportSelected} onChange={(e) => setReportSelected(e.target.checked)} />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PictureAsPdf fontSize="small" />
                    <Box>
                      <Typography variant="body1">PDF</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your one-page TrueSight report
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {!selectedCount
              ? 'Select at least one item to download'
              : gisSelected
                ? 'Your selection will be bundled into a zip'
                : 'Your report will download as a single PDF'}
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleDownload}
            disabled={!selectedCount || isDownloading}
            endIcon={<Download />}
            sx={{
              m: 3,
              minWidth: '105px',
              textAlign: 'center',
              backgroundColor: 'var(--gentian-blue)',
              color: 'var(--gentian-white)',
              '&:hover': {
                backgroundColor: 'var(--eclipse-black)',
              },
            }}
          >
            Download
          </Button>

          {isDownloading && <LinearProgress sx={{ width: '100%' }} variant="indeterminate" />}
        </Box>
      </Box>
    </Dialog>
  );
};
